import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Sun, Moon } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_4a46bb01-e425-48c8-a790-c185a7fd0b57/artifacts/6wdpibd5_logomedusa.jpeg';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Products', path: '/products' },
  { name: 'Catalogue', path: '/catalogue' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { isDarkMode, toggleDarkMode } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <>
      <motion.nav
        data-testid="navbar"
        className={`fixed w-full z-50 top-0 transition-all duration-300 ${isScrolled
            ? isDarkMode
              ? 'bg-zinc-900/90 backdrop-blur-md border-b border-white/5 shadow-sm'
              : 'bg-white/90 backdrop-blur-md border-b border-black/5 shadow-sm'
            : 'bg-transparent'
          }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
              <Link
                to="/"
                className="flex items-center gap-3"
                data-testid="logo-link"
              >
                <div className={`p-1 rounded ${isDarkMode ? 'bg-black' : 'bg-white'}`}>
                  <img
                    src={LOGO_URL}
                    alt="MALABISthestic"
                    className="w-10 h-10 md:w-12 md:h-12 object-contain"
                    style={{ filter: isDarkMode ? 'invert(1) sepia(0.3)' : 'none' }}
                  />
                </div>
              <span
                className={`text-lg md:text-xl tracking-widest hidden sm:block ${isDarkMode ? 'text-white' : ''}`}
                style={{ fontFamily: 'Bodoni Moda, serif' }}
              >
                MALABIS<span style={{ color: '#D4AF37' }}>theistic</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-10">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                  className={`nav-link ${isDarkMode
                      ? 'text-white hover:text-[#D4AF37]'
                      : location.pathname === link.path ? 'text-black' : 'text-gray-600'
                    }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Right side - Dark Mode Toggle, CTA and Mobile Menu */}
            <div className="flex items-center gap-4">
              {/* Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                className={`p-2 rounded-full transition-colors ${isDarkMode
                    ? 'bg-zinc-800 text-yellow-400 hover:bg-zinc-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                data-testid="dark-mode-toggle"
                aria-label="Toggle dark mode"
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>

              {/* Phone CTA - Desktop */}
              <div className="hidden md:flex items-center gap-4">
                <a
                  href="tel:+916398130510"
                  className={`flex items-center gap-2 text-xs uppercase tracking-wider hover:text-[#D4AF37] transition-colors ${isDarkMode ? 'text-white' : ''
                    }`}
                  data-testid="phone-cta"
                >
                  <Phone size={16} />
                  <span>6398130510</span>
                </a>
                <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-300'}`}>|</span>
                <a
                  href="tel:+917037738809"
                  className={`flex items-center gap-2 text-xs uppercase tracking-wider hover:text-[#D4AF37] transition-colors ${isDarkMode ? 'text-white' : ''
                    }`}
                  data-testid="phone-cta-2"
                >
                  <span>7037738809</span>
                </a>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`lg:hidden p-2 ${isDarkMode ? 'text-white' : ''}`}
                data-testid="mobile-menu-toggle"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            data-testid="mobile-menu"
            className={`fixed inset-0 z-40 lg:hidden ${isDarkMode ? 'bg-zinc-900' : 'bg-white'}`}
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <div className="flex flex-col pt-24 px-6">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={link.path}
                    className={`mobile-menu-link block hover:text-[#D4AF37] ${isDarkMode ? 'text-white border-gray-700' : 'text-black border-gray-300'
                      }`}
                    data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}

              {/* Dark Mode Toggle in Mobile */}
              <motion.div
                className="mt-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <button
                  onClick={toggleDarkMode}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg ${isDarkMode
                      ? 'bg-zinc-800 text-white'
                      : 'bg-gray-100 text-gray-800'
                    }`}
                >
                  {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                  <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
                </button>
              </motion.div>

              {/* Mobile Contact Info */}
              <motion.div
                className={`mt-8 pt-8 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <p className={`text-xs uppercase tracking-wider mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>Contact Us</p>
                <a
                  href="tel:+916398130510"
                  className={`flex items-center gap-2 text-lg mb-3 ${isDarkMode ? 'text-white' : ''}`}
                  data-testid="mobile-phone"
                >
                  <Phone size={18} />
                  +91 6398130510
                </a>
                <a
                  href="tel:+917037738809"
                  className={`flex items-center gap-2 text-lg mb-3 ${isDarkMode ? 'text-white' : ''}`}
                  data-testid="mobile-phone-2"
                >
                  <Phone size={18} />
                  +91 7037738809
                </a>
                <a
                  href="mailto:theisticmalabis@gmail.com"
                  className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
                  data-testid="mobile-email"
                >
                  theisticmalabis@gmail.com
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
